import type { Content, TDocumentDefinitions } from "pdfmake/interfaces";
import { getPdfMake } from "@/modules/proposals/pdf/initPdfMake";
import type { Company, Contact } from "@/components/atomic-crm/types";
import {
  formatBillToNameLine,
  resolveBillToDisplay,
} from "@/modules/billing/billingUtils";
import type { ClientInvoice, ClientInvoiceLineItem } from "@/modules/types";
import { computeInvoiceBalanceDue } from "@/modules/billing/invoicePaymentUtils";
import { TONE_HEX } from "@/modules/shared/status";

export type ClientInvoicePdfContext = {
  invoice: ClientInvoice;
  organizationName: string;
  organizationWebsite?: string | null;
  organizationAddress?: string | null;
  billToNameLine?: string | null;
  billToAddressLines?: string[];
  billToEmail?: string | null;
  billToPhone?: string | null;
  lineItems?: ClientInvoiceLineItem[];
};

export const buildClientInvoicePdfContext = ({
  invoice,
  organizationName,
  organizationAddress,
  organizationWebsite,
  company,
  contact,
  lineItems,
  billToEmail,
}: {
  invoice: ClientInvoice;
  organizationName: string;
  organizationAddress?: string | null;
  organizationWebsite?: string | null;
  company?: Company | null;
  contact?: Contact | null;
  lineItems?: ClientInvoiceLineItem[];
  billToEmail?: string;
}): ClientInvoicePdfContext => {
  const billTo = resolveBillToDisplay(company, contact);

  return {
    invoice,
    organizationName,
    organizationAddress,
    organizationWebsite,
    billToNameLine: formatBillToNameLine(company, contact),
    billToAddressLines: billTo.addressLines,
    billToEmail: billToEmail ?? billTo.email,
    billToPhone: billTo.phone,
    lineItems,
  };
};

const formatMoney = (amount: number, currency = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
    amount,
  );

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const buildLineItemsTable = (
  lineItems: ClientInvoiceLineItem[],
  fallbackDescription: string,
  fallbackAmount: number,
  currency: string,
): Content => {
  if (lineItems.length === 0) {
    return {
      table: {
        headerRows: 1,
        widths: [24, "*", 52, 52, 64],
        body: [
          [
            { text: "#", style: "tableHeaderDark" },
            { text: "Item & Description", style: "tableHeaderDark" },
            {
              text: "Qty",
              style: "tableHeaderDark",
              alignment: "right" as const,
            },
            {
              text: "Rate",
              style: "tableHeaderDark",
              alignment: "right" as const,
            },
            {
              text: "Amount",
              style: "tableHeaderDark",
              alignment: "right" as const,
            },
          ],
          [
            "1",
            fallbackDescription,
            { text: "1.00", alignment: "right" as const },
            {
              text: fallbackAmount.toFixed(2),
              alignment: "right" as const,
            },
            {
              text: formatMoney(fallbackAmount, currency),
              alignment: "right" as const,
            },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 0.5,
        vLineWidth: () => 0,
        hLineColor: () => TONE_HEX.border,
      },
    };
  }

  return {
    table: {
      headerRows: 1,
      widths: [24, "*", 52, 52, 64],
      body: [
        [
          { text: "#", style: "tableHeaderDark" },
          { text: "Item & Description", style: "tableHeaderDark" },
          {
            text: "Qty",
            style: "tableHeaderDark",
            alignment: "right" as const,
          },
          {
            text: "Rate",
            style: "tableHeaderDark",
            alignment: "right" as const,
          },
          {
            text: "Amount",
            style: "tableHeaderDark",
            alignment: "right" as const,
          },
        ],
        ...lineItems.map((line, index) => {
          const parts = String(line.description ?? "").split("\n");
          const title = parts[0] ?? "";
          const detail = parts.slice(1).join("\n");
          return [
            String(index + 1),
            detail
              ? {
                  stack: [
                    { text: title, bold: true },
                    { text: detail, fontSize: 9, color: TONE_HEX.muted },
                  ],
                }
              : title,
            {
              text: Number(line.quantity).toFixed(2),
              alignment: "right" as const,
            },
            {
              text: Number(line.unit_price).toFixed(2),
              alignment: "right" as const,
            },
            {
              text: formatMoney(Number(line.line_total) || 0, currency),
              alignment: "right" as const,
            },
          ];
        }),
      ],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0,
      hLineColor: () => TONE_HEX.border,
    },
  };
};

const buildDocDefinition = (
  ctx: ClientInvoicePdfContext,
): TDocumentDefinitions => {
  const {
    invoice,
    organizationName,
    organizationWebsite,
    organizationAddress,
    billToNameLine,
    billToAddressLines = [],
    billToEmail,
    billToPhone,
    lineItems = [],
  } = ctx;
  const currency = invoice.currency ?? "USD";
  const billToHeading = billToNameLine ?? "Client";
  const lineSubtotal = lineItems.reduce(
    (sum, line) => sum + Number(line.line_total || 0),
    0,
  );
  const subtotal =
    Number(invoice.subtotal ?? (lineSubtotal || invoice.amount)) || 0;
  const discountAmount = Number(invoice.discount_amount) || 0;
  const feeAmount = Number(invoice.fee_amount) || 0;
  const total = Number(invoice.amount) || subtotal - discountAmount + feeAmount;
  const balanceDue = computeInvoiceBalanceDue(
    total,
    Number(invoice.amount_paid) || 0,
  );

  const totalsRows: unknown[][] = [
    [
      { text: "Sub Total", color: TONE_HEX.muted },
      { text: formatMoney(subtotal, currency), alignment: "right" },
    ],
  ];
  if (discountAmount > 0) {
    totalsRows.push([
      { text: "Discount", color: TONE_HEX.muted },
      {
        text: `-${formatMoney(discountAmount, currency)}`,
        alignment: "right",
      },
    ]);
  }
  if (feeAmount > 0) {
    totalsRows.push([
      { text: "Transfer Fee", color: TONE_HEX.muted },
      { text: formatMoney(feeAmount, currency), alignment: "right" },
    ]);
  }
  totalsRows.push(
    [
      { text: "Total", bold: true },
      {
        text: formatMoney(total, currency),
        alignment: "right",
        bold: true,
      },
    ],
    [
      { text: "Balance Due", bold: true },
      {
        text: formatMoney(balanceDue, currency),
        alignment: "right",
        bold: true,
      },
    ],
  );

  return {
    pageSize: "LETTER",
    pageMargins: [48, 48, 48, 48],
    content: [
      {
        columns: [
          {
            width: "*",
            stack: [
              { text: "INVOICE", style: "invoiceTitle" },
              {
                text: `Invoice# ${invoice.invoice_number ?? ""}`,
                style: "invoiceNumber",
              },
              { text: " ", margin: [0, 8] },
              { text: "Balance Due", style: "label" },
              { text: formatMoney(balanceDue, currency), style: "balanceDue" },
            ],
          },
          {
            width: "auto",
            stack: [
              { text: organizationName, style: "orgName", alignment: "right" },
              ...(organizationAddress
                ? [
                    {
                      text: organizationAddress,
                      style: "muted",
                      alignment: "right" as const,
                    },
                  ]
                : []),
              ...(organizationWebsite
                ? [
                    {
                      text: organizationWebsite,
                      style: "link",
                      alignment: "right" as const,
                    },
                  ]
                : []),
            ],
          },
        ],
      },
      { text: " ", margin: [0, 16] },
      {
        columns: [
          {
            width: "*",
            stack: [
              { text: "Invoice Date", style: "label" },
              { text: formatDate(invoice.issue_date), style: "value" },
              { text: "Terms", style: "label", margin: [0, 8, 0, 0] },
              { text: invoice.terms ?? "Net 30", style: "value" },
              { text: "Due Date", style: "label", margin: [0, 8, 0, 0] },
              { text: formatDate(invoice.due_date), style: "value" },
              ...(invoice.reference?.trim()
                ? [
                    {
                      text: "PO / Ref",
                      style: "label",
                      margin: [0, 8, 0, 0],
                    },
                    { text: invoice.reference.trim(), style: "value" },
                  ]
                : []),
            ],
          },
          {
            width: "*",
            stack: [
              { text: "Bill To", style: "billToLabel" },
              { text: billToHeading, style: "billToName" },
              ...billToAddressLines.map((line) => ({
                text: line,
                style: "muted",
              })),
              ...(billToEmail ? [{ text: billToEmail, style: "muted" }] : []),
              ...(billToPhone ? [{ text: billToPhone, style: "muted" }] : []),
            ],
          },
        ],
      },
      { text: " ", margin: [0, 20] },
      buildLineItemsTable(lineItems, invoice.description, total, currency),
      { text: " ", margin: [0, 12] },
      {
        columns: [
          { width: "*", text: "" },
          {
            width: 200,
            table: {
              widths: ["*", "auto"],
              body: totalsRows,
            },
            layout: "noBorders",
          },
        ],
      },
      ...(invoice.notes?.trim()
        ? [
            { text: " ", margin: [0, 24] },
            {
              text: "Terms & Conditions",
              style: "termsHeading",
            },
            {
              text: invoice.notes.trim(),
              style: "termsBody",
            },
          ]
        : []),
    ] as Content[],
    styles: {
      orgName: { fontSize: 14, bold: true },
      invoiceTitle: { fontSize: 24, bold: true },
      invoiceNumber: {
        fontSize: 10,
        color: TONE_HEX.muted,
        margin: [0, 4, 0, 0],
      },
      balanceDue: { fontSize: 22, bold: true, margin: [0, 2, 0, 0] },
      label: { fontSize: 9, color: TONE_HEX.muted, margin: [0, 0, 0, 2] },
      value: { fontSize: 11 },
      billToLabel: { fontSize: 11, bold: true, margin: [0, 0, 0, 4] },
      billToName: { fontSize: 11, bold: true, color: TONE_HEX.brand },
      muted: { fontSize: 10, color: TONE_HEX.muted },
      link: { fontSize: 10, color: TONE_HEX.info },
      tableHeaderDark: {
        bold: true,
        color: "#ffffff",
        fillColor: TONE_HEX.brandNavy,
        margin: [4, 6, 4, 6],
        fontSize: 9,
      },
      termsHeading: {
        fontSize: 9,
        bold: true,
        color: TONE_HEX.muted,
        margin: [0, 0, 0, 6],
        characterSpacing: 0.5,
      },
      termsBody: { fontSize: 9, color: TONE_HEX.muted, lineHeight: 1.35 },
    },
    defaultStyle: { fontSize: 10, lineHeight: 1.25 },
  };
};

export const generateClientInvoicePdfBlob = async (
  ctx: ClientInvoicePdfContext,
): Promise<Blob> => {
  const doc = buildDocDefinition(ctx);
  const pdfMake = await getPdfMake();
  const pdf = pdfMake.createPdf(doc);
  return pdf.getBlob();
};

export const blobToBase64 = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

export const downloadClientInvoicePdf = async (
  ctx: ClientInvoicePdfContext,
) => {
  const blob = await generateClientInvoicePdfBlob(ctx);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${ctx.invoice.invoice_number ?? "invoice"}.pdf`;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const printClientInvoicePdf = async (ctx: ClientInvoicePdfContext) => {
  await downloadClientInvoicePdf(ctx);
};
